import type { IBrowserStorage, ILocation } from './types.ts';

const TEST_MODE_KEY = 'wl-test-mode';

/**
 * Initializes test mode based on the presence of a query parameter in the URL. Call it once on the app startup.
 *
 * Works additively: enabling test mode if the key present; not disabling otherwise.
 * This is needed when switching between V2 & V3 to preserve the state and don't bother about keeping query params.
 * To reset test mode, call with an empty or falsy key, or clear session storage directly.
 * @param key query param that indicates test mode should be enabled
 * @param sessionStorage Optional storage implementation, defaults to `window.sessionStorage`
 * @param location Optional location object, defaults to `window.location`
 */
export function initTestMode(
  key?: string,
  sessionStorage: IBrowserStorage = window.sessionStorage,
  location: ILocation = window.location,
): void {
  if (!key) {
    sessionStorage.removeItem(TEST_MODE_KEY);
    return;
  }

  const isEnabled = new URLSearchParams(location.search || '').has(key);
  if (isEnabled) {
    // eslint-disable-next-line no-console
    console.warn('Whitelabel test mode enabled');
    sessionStorage.setItem(TEST_MODE_KEY, 'true');
  }
}

/**
 * Checks if test mode is enabled
 * @param sessionStorage Optional storage implementation, defaults to `window.sessionStorage`
 */
export function getIsTestMode(sessionStorage: IBrowserStorage = window.sessionStorage): boolean {
  return sessionStorage.getItem(TEST_MODE_KEY) === 'true';
}
